import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import moment from 'moment';

import { dateHeader, formatCellDate } from './timeModeCell';
import { ColumnExtendedStrict } from '../../types/ColumnExtended';
import { JourneyFacility } from '../../components/pages/device/VisitJourneyMap/VisitJourneyPanel';
import { VisibilityOutlined } from '@mui/icons-material';

const calcDuration = (from: string, to: string): string => {
  const ms = moment(to).diff(moment(from));
  if (ms <= 0) return '—';
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return '<1m';
};

// Passes the unique rowId (not facilityId) so revisited facilities resolve to
// the exact journey step that was clicked.
export const makeVisitJourneyColumns = (
  onViewSightings: (rowId: string) => void,
  canViewSightings = true,
): ColumnExtendedStrict<JourneyFacility>[] => [
  {
    header: '#',
    id: 'index',
    size: 40,
    cell: ({ row }) => <span style={{ color: '#64748b', fontWeight: 500 }}>{row.original.journeyStep}</span>,
  },
  ...(canViewSightings
    ? [
        {
          id: 'actions',
          header: '',
          size: 50,
          cell: ({ row }: { row: { original: JourneyFacility } }) => (
            <Tooltip title='View Sightings'>
              <span>
                <IconButton
                  size='small'
                  disabled={!row.original.rowId}
                  onClick={(e) => {
                    e.stopPropagation();

                    if (!row.original.rowId) return;

                    onViewSightings(row.original.rowId);
                  }}
                  sx={{
                    color: '#475569',
                  }}
                >
                  <VisibilityOutlined fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          ),
        } as ColumnExtendedStrict<JourneyFacility>,
      ]
    : []),

  {
    header: 'Closest Facility',
    accessorKey: 'facilityName',
    enableColumnFilter: true,
    size: 200,
    cell: ({ row }) => (
      <Tooltip title={row.original.facilityName ?? ''} placement='top'>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, maxWidth: 180 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#dc2626', flexShrink: 0 }} />
          <Box
            component='span'
            sx={{
              color: '#dc2626',
              fontSize: 12,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.original.facilityName || '—'}
          </Box>
        </Box>
      </Tooltip>
    ),
  },
  {
    header: 'Station',
    accessorKey: 'stationIataCode',
    size: 80,
    cell: ({ row }) => (row.original.stationIataCode ? row.original.stationIataCode : '—'),
  },
  {
    header: dateHeader('First Seen'),
    accessorKey: 'firstSeenDateTime',
    size: 150,
    cell: ({ row, table }) => (row.original.firstSeenDateTime ? formatCellDate(table, row.original.firstSeenDateTime) : '—'),
  },
  {
    header: dateHeader('Last Seen'),
    accessorKey: 'lastSeenDateTime',
    enableColumnFilter: true,
    size: 150,
    cell: ({ row, table }) => (row.original.lastSeenDateTime ? formatCellDate(table, row.original.lastSeenDateTime) : '—'),
  },
  {
    id: 'duration',
    header: 'Duration',
    size: 90,
    cell: ({ row }) =>
      row.original.firstSeenDateTime && row.original.lastSeenDateTime
        ? calcDuration(row.original.firstSeenDateTime, row.original.lastSeenDateTime)
        : '—',
  },
  {
    header: 'Sightings',
    accessorKey: 'totalSightings',
    size: 80,
    cell: ({ row }) => <span style={{ color: '#0f172a', fontWeight: 600 }}>{row.original.totalSightings ?? 0}</span>,
  },
  {
    header: 'Avg Temp',
    accessorKey: 'averageTemperatureF',
    size: 90,
    cell: ({ row }) => (row.original.averageTemperatureF != null ? `${row.original.averageTemperatureF.toFixed(1)}°F` : '—'),
  },
];
